import { Alert, Input, Modal } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { BulkActionType, IBulkImpact } from '../../../services/common/common.model';
import {
  bulkActionLabel,
  formatImpactDimensions,
  isConfirmTextMatch,
} from '../../bulkImpact.helper';

export interface IBulkImpactConfirmModalProps {
  action: BulkActionType;
  impact: IBulkImpact;
  // Server message when a previous attempt was refused (HTTP 409) and the impact was re-checked.
  serverMessage?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DANGER_RED = '#cf1322';

// Typed-confirmation guard shown before an Update All / Delete All that spans more than one
// dataset (company / BU / tenant / date added). OK stays disabled until the user types the
// server-issued confirm text.
const BulkImpactConfirmModal: React.FC<IBulkImpactConfirmModalProps> = (props) => {
  const { action, impact, serverMessage, onConfirm, onCancel } = props;
  const [typed, setTyped] = useState('');

  useEffect(() => {
    setTyped('');
  }, [impact]);

  const label = bulkActionLabel(action);
  const matches = isConfirmTextMatch(typed, impact?.confirm_text);
  const dimensions = formatImpactDimensions(impact);

  return (
    <Modal
      visible={true}
      zIndex={1100}
      width={620}
      maskClosable={false}
      keyboard={true}
      onCancel={onCancel}
      onOk={() => matches && onConfirm()}
      okText={`${label} ${(impact?.records ?? 0).toLocaleString()} records`}
      okButtonProps={{ danger: true, disabled: !matches }}
      cancelText="Cancel"
      title={
        <span style={{ color: DANGER_RED, fontWeight: 700, fontSize: 18 }}>
          <WarningFilled style={{ marginRight: 8 }} />
          DANGER: {label} across multiple datasets
        </span>
      }
    >
      {serverMessage && (
        <Alert type="error" showIcon message={serverMessage} style={{ marginBottom: 16 }} />
      )}
      <div style={{ color: DANGER_RED, fontWeight: 700, fontSize: 22, lineHeight: 1.3 }}>
        This will {label.toUpperCase()} {(impact?.records ?? 0).toLocaleString()} records
      </div>
      {dimensions && (
        <div style={{ fontWeight: 600, fontSize: 16, marginTop: 8 }}>{dimensions}</div>
      )}
      {action === 'delete' && (
        <div style={{ color: DANGER_RED, fontWeight: 700, fontSize: 16, marginTop: 8 }}>
          This cannot be undone.
        </div>
      )}
      <div style={{ marginTop: 20, fontSize: 15 }}>
        Type <strong style={{ color: DANGER_RED }}>{impact?.confirm_text}</strong> to confirm:
      </div>
      <Input
        autoFocus
        size="large"
        value={typed}
        placeholder={impact?.confirm_text ?? ''}
        onChange={(e) => setTyped(e.target.value)}
        onPressEnter={() => matches && onConfirm()}
        style={{ marginTop: 8 }}
      />
    </Modal>
  );
};

export default BulkImpactConfirmModal;
